<script setup lang="ts">
import maplibregl, { Popup } from 'maplibre-gl'
import { storeToRefs } from 'pinia'
import {
  ref,
  shallowRef,
  inject,
  watch,
  onMounted,
  onUnmounted,
  computed,
  nextTick,
  type Ref,
  type ShallowRef,
} from 'vue'

import { isTouchDevice } from '@/services/uiService'
import { getWaypointIcon } from '@/services/waypointIconService'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour } from '@/types/tour'
import { hideMapHoverPopup, showMapHoverPopup } from '@/utils/mapHoverPopup'
import { createMapMarker, createMapMarkerElement } from '@/utils/mapMarkerElement'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import { buildWorkspaceWaypointHoverPopupHtml } from '@/utils/tourHoverPopup'
import { escapeHtml } from '@/utils/trackHoverPopup'

const props = defineProps<{
  tour: Tour
  selectedWaypointId?: string | null
}>()

const emit = defineEmits<{
  'waypoint-edit': [tourId: string, waypointIndex: number]
}>()

const uiStore = useUiStore()
const workspaceStore = useWorkspaceStore()
const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const mapLoaded = inject<Ref<boolean>>('mapLoaded')
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

const sourceId = computed(() => `waypoint-source-${props.tour.clientId}`)
const waypointPointLayerId = computed(() => `waypoint-point-${props.tour.clientId}`)
const waypointIconLayerId = computed(() => `waypoint-icon-${props.tour.clientId}`)

const selectedPopup = shallowRef<Popup | null>(null)
const selectedPopupId = ref<string | null>(null)
const hoverPopup = shallowRef<Popup | null>(null)
const hoverPopupId = ref<string | null>(null)

// Fix for TypeScript error: Use a more specific type for markers
// instead of using maplibregl.Marker which can cause type instantiation to be too deep
type SimpleMarker = {
  remove: () => void
  getLngLat: () => { lng: number; lat: number }
}
const markers = ref<SimpleMarker[]>([])
const markerElements = ref<Map<string, HTMLDivElement>>(new Map())

const { hoveredWaypointId, isTrackDrawingActive } = storeToRefs(uiStore)
const { selectedTourId, hoveredTourId, activelyHoveredTourId } = storeToRefs(workspaceStore)

const areTourWaypointsVisible = () => props.tour._waypointsVisible !== false
const isTourVisible = () => props.tour.visible !== false
const isSelectedTour = computed(() => selectedTourId.value === props.tour.clientId)
const tourColor = computed(() => resolveTourAccentColor(props.tour))
const isWaypointVisible = (waypointId: string) => !uiStore.isWaypointHidden(waypointId)

const shouldRenderWaypoints = () =>
  isTourVisible() &&
  areTourWaypointsVisible() &&
  uiStore.areWaypointsVisible &&
  !uiStore.areMapElementsForceHidden &&
  !uiStore.isInWorkspacesOverview

const applyWaypointHover = (waypointId: string) => {
  uiStore.setHoveredWaypoint(waypointId)
  workspaceStore.setActivelyHoveredTourId(props.tour.clientId)
  if (workspaceStore.hoveredTourId !== props.tour.clientId) {
    workspaceStore.setHoveredTourId(props.tour.clientId)
  }
}

const clearWaypointHover = () => {
  if (hoveredWaypointId.value?.startsWith(`${props.tour.clientId}_`)) {
    uiStore.setHoveredWaypoint(null)
  }

  workspaceStore.setActivelyHoveredTourId(null)
  if (workspaceStore.hoveredTourId === props.tour.clientId) {
    workspaceStore.setHoveredTourId(
      workspaceStore.selectedTourId === props.tour.clientId ? props.tour.clientId : null,
    )
  }
}

const updateWaypointPointHoverStyle = () => {
  if (isSelectedTour.value) return
  const map = mapInstance?.value
  if (!map?.getLayer(waypointPointLayerId.value)) return

  map.setPaintProperty(waypointPointLayerId.value, 'circle-radius-transition', {
    duration: 0,
    delay: 0,
  })
  map.setPaintProperty(waypointPointLayerId.value, 'circle-stroke-width-transition', {
    duration: 0,
    delay: 0,
  })
  map.setPaintProperty(waypointPointLayerId.value, 'circle-radius', [
    'case',
    activelyHoveredTourId.value === props.tour.clientId,
    6,
    4,
  ])
  map.setPaintProperty(waypointPointLayerId.value, 'circle-stroke-width', [
    'case',
    activelyHoveredTourId.value === props.tour.clientId,
    2.5,
    2,
  ])
}

const updateWaypointMarkerHoverStyle = () => {
  markerElements.value.forEach((markerEl, waypointId) => {
    const isWaypointHovered = hoveredWaypointId.value === waypointId
    const isWaypointSelected = props.selectedWaypointId === waypointId
    markerEl.style.transform = isWaypointHovered || isWaypointSelected ? 'scale(1.18)' : 'scale(1)'
    markerEl.style.transition = 'none'
  })
}

const getGeoJsonData = (): GeoJSON.FeatureCollection => {
  return {
    type: 'FeatureCollection',
    features: props.tour.waypoints
      .map((waypoint, index) => ({
        type: 'Feature' as const,
        properties: {
          id: `${props.tour.clientId}_${index}`,
          tourId: props.tour.clientId,
          waypointIndex: index,
          waypointName: waypoint.name || 'Waypoint',
          waypointType: waypoint.type || '',
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [waypoint.lon, waypoint.lat],
        },
      }))
      .filter(
        (f) =>
          typeof f.geometry.coordinates[0] === 'number' &&
          typeof f.geometry.coordinates[1] === 'number' &&
          isWaypointVisible(f.properties.id as string),
      ),
  } as GeoJSON.FeatureCollection
}

const buildSelectedWaypointPopupHtml = (waypoint: Tour['waypoints'][number]) =>
  `<span class="text-sm font-semibold text-gray-900">` +
  `${escapeHtml(waypoint.name || 'Waypoint')}` +
  `</span>`

// Remove all custom markers from the map
const removeCustomMarkers = () => {
  markers.value.forEach((marker) => marker.remove())
  markers.value = []
  markerElements.value.clear()
}

const addClusterSource = () => {
  if (isSelectedTour.value) return
  const map = mapInstance?.value
  if (!map) return

  if (map.getSource(sourceId.value)) {
    const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
    source.setData(getGeoJsonData())
  } else {
    map.addSource(sourceId.value, {
      type: 'geojson',
      data: getGeoJsonData(),
    })
  }
}

const createClusterLayers = () => {
  if (isSelectedTour.value) return
  const map = mapInstance?.value
  if (!map) return

  if (!map.getLayer(waypointPointLayerId.value)) {
    map.addLayer({
      id: waypointPointLayerId.value,
      type: 'circle',
      source: sourceId.value,
      paint: {
        'circle-color': tourColor.value,
        'circle-radius': ['case', activelyHoveredTourId.value === props.tour.clientId, 6, 4],
        'circle-stroke-width': [
          'case',
          activelyHoveredTourId.value === props.tour.clientId,
          2.5,
          2,
        ],
        'circle-stroke-color': '#FFFFFF',
        'circle-opacity': 1,
        'circle-stroke-opacity': 1,
      },
    })
  }

  // We don't need an icon layer anymore as we'll use HTML markers
  // Instead, add custom HTML markers for non-clustered points
}

const setupEventHandlers = () => {
  if (isSelectedTour.value) return
  const map = mapInstance?.value
  if (!map) return

  // Hover handling - skip popups on touch devices
  map.on('mouseenter', waypointPointLayerId.value, (e) => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = 'pointer'

    // Don't show popup on touch devices
    if (isTouchDevice()) return

    const features = map.queryRenderedFeatures(e.point, { layers: [waypointPointLayerId.value] })
    if (!features.length) return

    const props = features[0].properties
    if (props) {
      applyWaypointHover(props.id as string)
    }
  })

  map.on('click', waypointPointLayerId.value, (e) => {
    if (isTrackDrawingActive.value) return
    const features = map.queryRenderedFeatures(e.point, { layers: [waypointPointLayerId.value] })
    if (!features.length) return
    const waypointIndex = Number(features[0].properties?.waypointIndex ?? -1)
    if (waypointIndex < 0) return
    emit('waypoint-edit', props.tour.clientId, waypointIndex)
  })

  map.on('mouseleave', waypointPointLayerId.value, () => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = ''
    clearWaypointHover()
  })
}

const showSelectedPopup = (waypointId: string) => {
  const map = mapInstance?.value
  if (!map) return

  if (selectedPopupId.value === waypointId) return

  selectedPopup.value = hideMapHoverPopup(selectedPopup.value)

  selectedPopupId.value = waypointId
  const waypointIndex = Number(waypointId.split('_').at(-1) ?? -1)
  const waypoint =
    Number.isInteger(waypointIndex) && waypointIndex >= 0
      ? props.tour.waypoints[waypointIndex]
      : null
  if (!waypoint || !Number.isFinite(waypoint.lon) || !Number.isFinite(waypoint.lat)) {
    selectedPopupId.value = null
    return
  }

  selectedPopup.value = showMapHoverPopup({
    map,
    popup: selectedPopup.value,
    lngLat: [waypoint.lon, waypoint.lat],
    html: buildSelectedWaypointPopupHtml(waypoint),
    className: 'waypoint-popup',
    offset: 22,
  })
}

const hideSelectedPopup = () => {
  selectedPopup.value = hideMapHoverPopup(selectedPopup.value)
  selectedPopupId.value = null
}

const showHoverPopup = (waypointId: string) => {
  const map = mapInstance?.value
  if (!map) return
  if (hoverPopupId.value === waypointId) return

  hoverPopup.value = hideMapHoverPopup(hoverPopup.value)

  hoverPopupId.value = waypointId
  const waypointIndex = Number(waypointId.split('_').at(-1) ?? -1)
  const waypoint =
    Number.isInteger(waypointIndex) && waypointIndex >= 0
      ? props.tour.waypoints[waypointIndex]
      : null
  if (!waypoint || !Number.isFinite(waypoint.lon) || !Number.isFinite(waypoint.lat)) {
    hoverPopupId.value = null
    return
  }

  const popupHtml = isSelectedTour.value
    ? buildSelectedWaypointPopupHtml(waypoint)
    : buildWorkspaceWaypointHoverPopupHtml(props.tour, waypoint)

  hoverPopup.value = showMapHoverPopup({
    map,
    popup: hoverPopup.value,
    lngLat: [waypoint.lon, waypoint.lat],
    html: popupHtml,
    className: 'waypoint-popup',
    offset: 22,
  })
}

const hideHoverPopup = () => {
  hoverPopup.value = hideMapHoverPopup(hoverPopup.value)
  hoverPopupId.value = null
}

const syncWayointPopups = () => {
  if (isTouchDevice() || !shouldRenderWaypoints()) {
    hideHoverPopup()
    hideSelectedPopup()
    return
  }

  const hoveredId = hoveredWaypointId.value?.startsWith(`${props.tour.clientId}_`)
    ? hoveredWaypointId.value
    : null
  const selectedId = props.selectedWaypointId?.startsWith(`${props.tour.clientId}_`)
    ? props.selectedWaypointId
    : null

  if (selectedId && isWaypointVisible(selectedId)) {
    showSelectedPopup(selectedId)
  } else {
    hideSelectedPopup()
  }

  if (hoveredId && hoveredId !== selectedId && isWaypointVisible(hoveredId)) {
    showHoverPopup(hoveredId)
  } else {
    hideHoverPopup()
  }
}

type MarkerRefreshMode = 'none' | 'immediate' | 'debounced'

const refreshWaypointMarkers = (mode: MarkerRefreshMode) => {
  if (mode === 'none') return

  if (mode === 'immediate') {
    clearMarkerRecreationTimeout()
    addWaypointMarkers()
    return
  }

  debouncedAddWaypointMarkers()
}

const updateLayerVisibility = (markerRefreshMode: MarkerRefreshMode = 'debounced') => {
  const map = mapInstance?.value
  if (!map) return

  const visible = shouldRenderWaypoints()
  const pointVisibility = visible && !isSelectedTour.value ? 'visible' : 'none'

  if (map.getLayer(waypointPointLayerId.value)) {
    map.setLayoutProperty(waypointPointLayerId.value, 'visibility', pointVisibility)
  }

  if (map.getLayer(waypointIconLayerId.value)) {
    map.setLayoutProperty(waypointIconLayerId.value, 'visibility', pointVisibility)
  }

  // Keep HTML markers in sync with the layer visibility state.
  if (visible && isSelectedTour.value) {
    refreshWaypointMarkers(markerRefreshMode)
  } else {
    // If becoming invisible, immediately remove all custom markers
    clearMarkerRecreationTimeout()
    removeCustomMarkers()
  }

  if (!visible) {
    hideHoverPopup()
    hideSelectedPopup()
  }
}

const removeLayers = () => {
  const map = mapInstance?.value
  if (!map) return

  hideHoverPopup()
  hideSelectedPopup()

  if (map.getLayer(waypointPointLayerId.value)) {
    map.removeLayer(waypointPointLayerId.value)
  }

  if (map.getLayer(waypointIconLayerId.value)) {
    map.removeLayer(waypointIconLayerId.value)
  }

  if (map.getSource(sourceId.value)) {
    map.removeSource(sourceId.value)
  }
}

const addWaypointMarkers = () => {
  const map = mapInstance?.value
  if (!map) return

  // Remove existing markers
  removeCustomMarkers()

  if (!shouldRenderWaypoints()) {
    return
  }

  if (!isSelectedTour.value) {
    return
  }

  props.tour.waypoints.forEach((waypoint, waypointIndex) => {
    if (!Number.isFinite(waypoint.lon) || !Number.isFinite(waypoint.lat)) return
    const waypointId = `${props.tour.clientId}_${waypointIndex}`
    if (!isWaypointVisible(waypointId)) return

    // Create marker element with Lucide icon
    const { element, touchArea, markerEl } = createMapMarkerElement(
      getWaypointIcon(waypoint.type || ''),
      tourColor.value,
      'waypoint-marker-container',
    )
    markerElements.value.set(waypointId, markerEl)

    // Add click handler to the touch area
    touchArea.addEventListener('click', function (event) {
      if (isTrackDrawingActive.value) return
      event.preventDefault()
      event.stopPropagation()
      emit('waypoint-edit', props.tour.clientId, waypointIndex)
    })

    // Add touch event listeners for better mobile support
    touchArea.addEventListener('touchstart', function (event) {
      if (isTrackDrawingActive.value) return
      event.preventDefault()
      event.stopPropagation()
      // Add visual feedback on touch start
      touchArea.style.opacity = '0.7'
    })

    touchArea.addEventListener('touchend', function (event) {
      if (isTrackDrawingActive.value) return
      event.preventDefault()
      event.stopPropagation()
      // Remove visual feedback
      touchArea.style.opacity = '1'
      // Trigger the waypoint edit action
      emit('waypoint-edit', props.tour.clientId, waypointIndex)
    })

    touchArea.addEventListener('touchcancel', function (event) {
      if (isTrackDrawingActive.value) return
      event.preventDefault()
      event.stopPropagation()
      // Remove visual feedback on cancel
      touchArea.style.opacity = '1'
    })

    // Add hover handler to the touch area - skip popups on touch devices
    touchArea.addEventListener('mouseenter', function () {
      if (isTrackDrawingActive.value) return
      // Don't show popup on touch devices
      if (!isTouchDevice()) {
        applyWaypointHover(waypointId)
      }
    })

    touchArea.addEventListener('mouseleave', function () {
      if (isTrackDrawingActive.value) return
      if (props.selectedWaypointId === waypointId) return
      clearWaypointHover()
    })

    // Create marker and add to map
    const marker = createMapMarker({
      element,
      anchor: 'center',
    })
      .setLngLat([waypoint.lon, waypoint.lat])
      .addTo(map)

    // Using a type assertion to avoid TypeScript's deep instantiation error
    markers.value.push(marker as SimpleMarker)
  })

  updateWaypointMarkerHoverStyle()

  syncWayointPopups()
}

const eventHandlers = {
  moveend: null as (() => void) | null,
  zoomend: null as (() => void) | null,
  idle: null as (() => void) | null,
}

// Debouncing for marker recreation to prevent rapid updates
let markerRecreationTimeout: number | null = null
const clearMarkerRecreationTimeout = () => {
  if (markerRecreationTimeout) {
    clearTimeout(markerRecreationTimeout)
    markerRecreationTimeout = null
  }
}

const debouncedAddWaypointMarkers = () => {
  clearMarkerRecreationTimeout()
  markerRecreationTimeout = window.setTimeout(() => {
    addWaypointMarkers()
    markerRecreationTimeout = null
  }, 200) // 200ms debounce
}

const forceRemoveMarkers = () => {
  clearMarkerRecreationTimeout()
  removeCustomMarkers()
  clearWaypointHover()
  hideHoverPopup()
  hideSelectedPopup()
}

defineExpose({
  forceRemoveMarkers,
})

const initializeLayers = () => {
  const map = mapInstance?.value
  const loaded = mapLoaded?.value

  if (!map || !loaded) {
    return
  }

  clearMarkerRecreationTimeout()
  if (eventHandlers.moveend) map.off('moveend', eventHandlers.moveend)
  if (eventHandlers.zoomend) map.off('zoomend', eventHandlers.zoomend)
  if (eventHandlers.idle) map.off('idle', eventHandlers.idle)
  eventHandlers.moveend = null
  eventHandlers.zoomend = null
  eventHandlers.idle = null

  if (isSelectedTour.value) {
    removeLayers()
    addWaypointMarkers()
    return
  }

  addClusterSource()
  createClusterLayers()
  setupEventHandlers()
  updateLayerVisibility('none')

  if (shouldRenderWaypoints()) {
    addWaypointMarkers()
  }

  // Add event listeners to update markers when the map moves/zooms (debounced)
  eventHandlers.moveend = debouncedAddWaypointMarkers
  eventHandlers.zoomend = debouncedAddWaypointMarkers
  eventHandlers.idle = () => {
    if (map.getSource(sourceId.value)) {
      debouncedAddWaypointMarkers()
    }
  }

  map.on('moveend', eventHandlers.moveend)
  map.on('zoomend', eventHandlers.zoomend)
  map.on('idle', eventHandlers.idle)
}

onMounted(() => {
  watch(
    mapLoaded ?? ref(false),
    (isLoaded) => {
      if (isLoaded && mapInstance?.value) {
        initializeLayers()
      }
    },
    { immediate: true },
  )
})

watch(hoveredWaypointId, () => {
  updateWaypointMarkerHoverStyle()
  syncWayointPopups()
})

watch(
  () => props.selectedWaypointId ?? null,
  () => {
    updateWaypointMarkerHoverStyle()
    syncWayointPopups()
  },
  { immediate: true },
)

watch([hoveredTourId, activelyHoveredTourId], () => {
  if (!isSelectedTour.value) {
    updateWaypointPointHoverStyle()
  }
})

watch(
  () => uiStore.hiddenWaypointIds,
  () => {
    if (
      (selectedPopupId.value && uiStore.isWaypointHidden(selectedPopupId.value)) ||
      (hoverPopupId.value && uiStore.isWaypointHidden(hoverPopupId.value))
    ) {
      clearWaypointHover()
      hideHoverPopup()
      hideSelectedPopup()
    }
    const map = mapInstance?.value
    if (map && mapLoaded?.value && map.getSource(sourceId.value)) {
      const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
      source.setData(getGeoJsonData())
    }
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
  },
  { deep: true },
)

watch(
  () => props.tour.visible,
  (newVisible) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
    if (!newVisible) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => props.tour._waypointsVisible,
  (newVisible) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
    if (newVisible === false) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => uiStore.areWaypointsVisible,
  (newVisible) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
    if (!newVisible) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => uiStore.areMapElementsForceHidden,
  (newHidden) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
    if (newHidden) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => selectedTourId.value,
  () => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
  },
)

// Watch for workspaces overview state changes
watch(
  () => uiStore.isInWorkspacesOverview,
  (newInOverview, oldInOverview) => {
    if (mapInstance?.value && mapLoaded?.value) {
      updateLayerVisibility('immediate')
    }
    if (newInOverview && !oldInOverview) {
      forceRemoveMarkers()
    }
  },
)

watch(
  () => props.tour.waypoints,
  () => {
    const map = mapInstance?.value
    if (!map || !mapLoaded?.value) {
      return
    }

    if (map.getSource(sourceId.value)) {
      const source = map.getSource(sourceId.value) as maplibregl.GeoJSONSource
      source.setData(getGeoJsonData())
    }

    // Recreate HTML markers after waypoint edits, including type/icon changes
    // while a selected tour is rendering marker instances directly.
    nextTick(() => {
      if (shouldRenderWaypoints()) {
        debouncedAddWaypointMarkers()
      }
    })
  },
  { deep: true },
)

watch(
  () => mapStyleRevision.value,
  () => {
    if (!mapInstance?.value || !mapLoaded?.value) {
      return
    }

    initializeLayers()
  },
)

watch(
  () => tourColor.value,
  () => {
    if (!mapInstance?.value || !mapLoaded?.value) return
    initializeLayers()
  },
)

onUnmounted(() => {
  forceRemoveMarkers()

  // Remove event listeners before layers to prevent errors
  const map = mapInstance?.value
  if (map) {
    if (eventHandlers.moveend) map.off('moveend', eventHandlers.moveend)
    if (eventHandlers.zoomend) map.off('zoomend', eventHandlers.zoomend)
    if (eventHandlers.idle) map.off('idle', eventHandlers.idle)
  }

  removeLayers()
})
</script>

<template>
  <div class="hidden"></div>
</template>
